import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getTVPlans, verifySmartcard, subscribeTv } from "../../api/apiServices";
import { useAuth } from "../../context/useAuth";

const PROVIDERS = [
  { id: "DSTV", label: "DStv", color: "#1D4ED8", bg: "#EFF6FF" },
  { id: "GOTV", label: "GOtv", color: "#DC2626", bg: "#FEF2F2" },
  { id: "STARTIMES", label: "Startimes", color: "#D97706", bg: "#FFFBEB" },
  { id: "SHOWMAX", label: "Showmax", color: "#7C3AED", bg: "#F5F3FF" },
];

export default function TVPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [provider, setProvider] = useState(searchParams.get("provider") || "DSTV");
  const [smartcardNo, setSmartcardNo] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null); // { customerName, currentBouquet, dueDate }
  const [loading, setLoading] = useState(false);

  const isValid = smartcardNo.length >= 10 && !!selectedPlan && !!accountInfo;

  // Fetch live plans whenever provider changes
  useEffect(() => {
    const fetchPlans = async () => {
      setPlansLoading(true);
      setSelectedPlan(null);
      setPlans([]);
      try {
        const res = await getTVPlans(provider);
        if (res.status) {
          setPlans(res.plans || []);
        } else {
          toast.error("Could not load plans");
        }
      } catch (err) {
        // toast.error("Failed to fetch TV plans");
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();
  }, [provider]);

  const handleVerifySmartcard = async () => {
    if (smartcardNo.length < 10 || !provider) return;
    setVerifying(true);
    setAccountInfo(null);
    try {
      const res = await verifySmartcard(provider, smartcardNo);
      if (res.status) {
        setAccountInfo({
          customerName: res.customerName,
          currentBouquet: res.currentBouquet,
          dueDate: res.dueDate,
          renewalAmount: res.renewalAmount,
        });
      } else {
        toast.error(res.message || "Smartcard not found");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Smartcard verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const num = "07014970207"

  const handlePay = async () => {
    if (!isValid || loading) return;
    setLoading(true);
    try {
      const res = await subscribeTv(
        provider,
        smartcardNo,
        selectedPlan.variationCode,
        Number(selectedPlan.amount),
        // user?.phone
        Number(num)
      );

      if (res.status) {
        navigate("/bills/success", {
          state: {
            type: "tv",
            title: `${provider} ${selectedPlan.name}`,
            detail: `Smartcard: ${smartcardNo}`,
            subDetail: accountInfo.customerName,
            amount: Number(selectedPlan.amount),
            requestId: res.requestId,
            transactionId: res.transactionId,
          },
        });
      } else {
        toast.error(res.message || "TV subscription failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "TV subscription failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-10">
      {/* Header */}
      <div className="bg-white px-5 pt-12 pb-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/bills")} className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
            <i className="ri-arrow-left-line text-slate-600 text-lg" />
          </button>
          <div>
            <h1 className="text-slate-800 font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Cable TV</h1>
            <p className="text-slate-400 text-xs">Subscribe or renew your cable</p>
          </div>
        </div>
      </div>

      <div className="px-5 pt-6 space-y-5">
        {/* Provider */}
        <div>
          <p className="text-slate-600 text-sm font-semibold mb-3">Select Provider</p>
          <div className="grid grid-cols-4 gap-2">
            {PROVIDERS.map((p) => (
              <button
                key={p.id}
                onClick={() => { setProvider(p.id); setAccountInfo(null); setSelectedPlan(null); }}
                className={`flex flex-col items-center gap-2 py-3 rounded-2xl border-2 transition-all ${
                  provider === p.id ? "border-emerald-500 bg-emerald-50" : "border-slate-100 bg-white"
                }`}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: p.bg }}>
                  <i className="ri-tv-2-line text-sm" style={{ color: p.color }} />
                </div>
                <span className="text-[10px] font-bold text-slate-700">{p.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Smartcard */}
        <div>
          <p className="text-slate-600 text-sm font-semibold mb-3">Smartcard / IUC Number</p>
          <div className="bg-white border-2 border-slate-200 rounded-2xl flex items-center px-4 focus-within:border-emerald-400 transition-colors">
            <i className="ri-tv-2-line text-slate-400 mr-3" />
            <input
              type="text"
              value={smartcardNo}
              onChange={(e) => {
                setSmartcardNo(e.target.value.replace(/\D/g, "").slice(0, 12));
                setAccountInfo(null);
              }}
              onBlur={handleVerifySmartcard}
              placeholder="Enter smartcard number"
              className="flex-1 py-4 outline-none text-slate-800 text-sm bg-transparent placeholder-slate-300"
            />
            {verifying && <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />}
            {accountInfo && !verifying && <i className="ri-checkbox-circle-fill text-emerald-500 text-lg" />}
          </div>

          {accountInfo && (
            <div className="mt-2 px-4 py-3 bg-emerald-50 rounded-xl border border-emerald-200 space-y-1">
              <p className="text-emerald-700 text-xs font-bold">{accountInfo.customerName}</p>
              {accountInfo.currentBouquet && (
                <p className="text-slate-500 text-xs">Current: {accountInfo.currentBouquet}</p>
              )}
              {accountInfo.dueDate && (
                <p className="text-slate-500 text-xs">Due: {accountInfo.dueDate}</p>
              )}
            </div>
          )}
        </div>

        {/* Plans */}
        <div>
          <p className="text-slate-600 text-sm font-semibold mb-3">Choose Plan</p>

          {plansLoading ? (
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-16 bg-white rounded-2xl border border-slate-100 animate-pulse" />
              ))}
            </div>
          ) : plans.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center">
              <p className="text-slate-400 text-sm">No plans available for {provider}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {plans.map((plan) => (
                <button
                  key={plan.variationCode}
                  onClick={() => setSelectedPlan(plan)}
                  className={`w-full flex items-center justify-between p-4 rounded-2xl border-2 transition-all ${
                    selectedPlan?.variationCode === plan.variationCode
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-slate-100 bg-white"
                  }`}
                >
                  <div className="flex items-center gap-3 text-left">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      selectedPlan?.variationCode === plan.variationCode ? "border-emerald-500" : "border-slate-300"
                    }`}>
                      {selectedPlan?.variationCode === plan.variationCode && (
                        <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full" />
                      )}
                    </div>
                    <p className="font-semibold text-slate-800 text-sm">{plan.name}</p>
                  </div>
                  <span className="font-bold text-emerald-600 text-sm">₦{Number(plan.amount).toLocaleString()}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={handlePay}
          disabled={!isValid || loading}
          className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2"
        >
          {loading ? (
            <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Processing…</>
          ) : isValid ? `Subscribe for ₦${Number(selectedPlan.amount).toLocaleString()}` : "Fill in details to continue"}
        </button>
      </div>
    </div>
  );
}
